import React from "react";
import previewAvatar from "../assets/optimized/profile-avatar-96.webp";
import { Icon } from "./Icon";

export const About = ({ data }) => {
  const retentionTitleLines = data?.retentionTitleLines?.length
    ? data.retentionTitleLines
    : ["Converted for you.", "Not archived by us."];
  const retentionParagraphs = data?.retentionParagraphs?.length
    ? data.retentionParagraphs
    : [
        "We process your audio and images to send text back to you on WhatsApp. Temporary content files are deleted after processing.",
        "We keep the account and usage details needed to run the service. Our Privacy Policy explains how Echo MSG and its service providers handle your data.",
      ];
  const rawWaveformBars = [
    16, 26, 34, 28, 22, 30, 38, 30, 24, 30, 26, 22, 18, 22, 28, 34, 26, 22,
    18, 16,
  ];
  const cleanWaveformBars = [
    16, 24, 36, 22, 30, 40, 24, 18, 34, 42, 30, 24, 20, 28, 36, 24, 18, 16,
  ];

  return (
    <section id="about" className="landing-section comparison-section">
      <div className="container">
        <div className="section-heading text-center comparison-heading">
          <h2>
            {data?.title ||
              "Text you can read and use"}
          </h2>
          <p>
            {data?.paragraph ||
              "Turn spoken updates into readable messages and get copyable text from screenshots and photos. Here's how Echo MSG handles a voice note."}
          </p>
        </div>

        <div className="row comparison-grid">
          <div className="col-md-6">
            <div className="comparison-column">
              <h3 className="comparison-column-title">
                {data?.originalLabel || "Generic transcript"}
              </h3>

              <div className="comparison-demo comparison-demo-raw">
                <div className="comparison-raw-connected message-bubble-received">
                  <div className="comparison-audio-inline">
                    <div className="comparison-audio-main">
                      <span className="comparison-avatar comparison-avatar-generic">
                        <Icon name="user" />
                      </span>
                      <span
                        className="comparison-play-icon"
                        aria-hidden="true"
                      ></span>
                      <div className="comparison-waveform" aria-hidden="true">
                        {rawWaveformBars.map((height, index) => (
                          <span
                            key={`raw-${height}-${index}`}
                            className="comparison-wave-bar"
                            style={{ height: `${height}px` }}
                          ></span>
                        ))}
                      </div>
                    </div>
                    <div className="comparison-audio-meta">
                      <span>0:43</span>
                      <span>
                        08:43{" "}
                        <span className="comparison-audio-ticks">✓✓</span>
                      </span>
                    </div>
                  </div>

                  <div className="comparison-message comparison-message-raw comparison-message-raw-connected">
                    <p>
                      {data?.originalText ||
                        "\"Hey so uh the meeting with Asterra is at ten forty _____ Tuesday and um we need to prep the _____ deck before then. The MRR projections are uh looking good but _____ wants to see the, the quarterly _____ before he signs off. Can you just, um, can you send that over?\""}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="comparison-column">
              <h3 className="comparison-column-title">
                {data?.resultLabel || "Echo MSG"}
              </h3>

              <div className="comparison-demo comparison-demo-clean">
                <div className="preview-audio-bubble comparison-preview-audio-bubble message-bubble-sent">
                  <div className="preview-bubble-meta">Forwarded</div>
                  <div className="preview-audio-row">
                    <img
                      src={previewAvatar}
                      alt="Profile"
                      className="preview-avatar comparison-preview-avatar"
                      decoding="async"
                      height="96"
                      loading="lazy"
                      width="96"
                    />
                    <div className="preview-play-button">
                      <span className="preview-play-icon" aria-hidden="true"></span>
                    </div>
                    <div className="preview-waveform" aria-hidden="true">
                      {cleanWaveformBars.map((height, index) => (
                        <span
                          key={`clean-${height}-${index}`}
                          className="preview-wave-bar"
                          style={{ height: `${height}px` }}
                        ></span>
                      ))}
                    </div>
                  </div>
                  <div className="preview-audio-footer">
                    <span>0:43</span>
                    <span className="preview-audio-status">
                      <span>08:43</span>
                      <span className="preview-checks">✓✓</span>
                    </span>
                  </div>
                </div>

                <div className="comparison-message comparison-message-clean message-bubble-received">
                  <div className="preview-transcription-copy comparison-transcription-copy">
                    <p>
                      {data?.resultText ||
                        "The call with Asterra is at 10:45 next Tuesday. We need the SOC 2 deck before then. Priya wants the MRR update included, and Jamie needs the final MSA before he signs. Can you send that over?"}
                    </p>
                  </div>
                  <div className="preview-text-meta comparison-message-time">08:43</div>
                </div>
              </div>

              <div className="comparison-proof-copy">
                <span className="comparison-proof-label">
                  {data?.proofLabel || "Why it matters"}
                </span>
                <p>
                  {data?.proofText ||
                    "Read names, times, and next steps in one place, then copy the details you need."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="comparison-retention-section" aria-label="No stored content archive">
          <div className="comparison-retention-grid">
            <div className="comparison-retention-copy">
              <span className="comparison-retention-badge">
                <span className="comparison-retention-badge-dot" aria-hidden="true"></span>
                {data?.retentionBadge || "No stored content archive"}
              </span>

              <h3 className="comparison-retention-title">
                {retentionTitleLines.map((line, index) => (
                  <span
                    key={`${line}-${index}`}
                    className={
                      index === retentionTitleLines.length - 1
                        ? "comparison-retention-title-line comparison-retention-title-line-accent"
                        : "comparison-retention-title-line"
                    }
                  >
                    {line}
                  </span>
                ))}
              </h3>

              <p className="comparison-retention-lead">
                {data?.retentionLead ||
                  "We don't keep an archive of your voice notes, images, or converted text in our own database."}
              </p>

              <div className="comparison-retention-paragraphs">
                {retentionParagraphs.map((paragraph, index) => (
                  <p key={`${paragraph}-${index}`}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div className="comparison-retention-visual" aria-hidden="true">
              <div className="comparison-retention-visual-shell">
                <div className="comparison-retention-lock-card">
                  <span className="comparison-retention-lock-dot"></span>
                  <div className="comparison-retention-lock-icon">
                    <span className="comparison-retention-lock-shackle"></span>
                    <span className="comparison-retention-lock-body">
                      <span className="comparison-retention-lock-keyhole"></span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};
